/** How long a skeleton stays on screen once it has appeared (P13).
 *
 * A skeleton is shown only after 150ms of loading (the bones fade in after a 150ms CSS delay, so a read
 * that answers faster never flashes one) and, once it has been seen, stays at least 300ms so it does
 * not blink out. This is the second half: `useSkeletonHold(loading)` is true while loading, and for
 * the remainder of that 300ms after loading ends, but only if the bones had actually appeared. A read
 * that finished inside 150ms returns false at once, which is what keeps tests and fast reads instant.
 *
 * It is a small external store read with `useSyncExternalStore`, with the `loading` flag folded into
 * the snapshot, so the render in which loading ends is already the held one (no frame of the real
 * screen between the bones and the bones).
 */

import { useState, useSyncExternalStore } from "react";

export const SKELETON_DELAY_MS = 150;
export const SKELETON_MIN_VISIBLE_MS = 300;

/** Milliseconds still owed after loading ended, given how long it lasted. Zero when the bones never showed. */
export function skeletonHoldRemaining(loadedAfterMs: number): number {
  if (loadedAfterMs <= SKELETON_DELAY_MS) return 0;
  return Math.max(0, SKELETON_DELAY_MS + SKELETON_MIN_VISIBLE_MS - loadedAfterMs);
}

function createHold() {
  let startedAt: number | null = null;
  let holdUntil: number | null = null;
  let timer: number | null = null;
  const listeners = new Set<() => void>();

  return {
    subscribe: (listener: () => void): (() => void) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
        if (listeners.size === 0 && timer !== null) {
          window.clearTimeout(timer);
          timer = null;
        }
      };
    },
    snapshot: (loading: boolean): boolean => {
      const now = Date.now();
      if (loading) {
        startedAt ??= now;
        holdUntil = null;
        return true;
      }
      if (startedAt !== null) {
        const remaining = skeletonHoldRemaining(now - startedAt);
        startedAt = null;
        if (remaining > 0) {
          holdUntil = now + remaining;
          timer = window.setTimeout(() => {
            timer = null;
            holdUntil = null;
            listeners.forEach((listener) => listener());
          }, remaining);
        }
      }
      return holdUntil !== null && now < holdUntil;
    },
  };
}

export function useSkeletonHold(loading: boolean): boolean {
  const [hold] = useState(createHold);
  return useSyncExternalStore(
    hold.subscribe,
    () => hold.snapshot(loading),
    () => loading,
  );
}
